/**
 * Textarea — bottom-border, mono, transparent. Pair with `<Field span>` so it
 * spans the full grid row.
 *
 * Pass `autoResize` to grow the field to fit its content (no inner scrollbar);
 * `rows` then acts as the minimum height. Works with controlled values and with
 * react-hook-form's `register` (uncontrolled) alike.
 *
 * Source: extrapolated from artboard-radical-zakaznik-form.jsx:188-203
 */

import {
  useCallback,
  useLayoutEffect,
  useRef,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
  /** Grow to fit content as the user types; `rows` becomes the minimum height. */
  autoResize?: boolean;
}

export function Textarea({
  className = '',
  rows = 3,
  autoResize = false,
  ref,
  onChange,
  value,
  style,
  ...rest
}: TextareaProps) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = node;
    },
    [ref],
  );

  const resize = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Resize on mount and whenever a controlled value changes. For RHF
  // (uncontrolled) the value is set on the element by the time layout effects
  // run, so the initial fit is correct; subsequent typing fires onChange below.
  useLayoutEffect(() => {
    if (autoResize) resize();
  }, [autoResize, resize, value]);

  return (
    <textarea
      {...rest}
      value={value}
      ref={setRefs}
      rows={rows}
      onChange={(e) => {
        onChange?.(e);
        if (autoResize) resize();
      }}
      style={
        autoResize
          ? { minHeight: `calc(${rows} * 1.55em + 0.5rem)`, ...style }
          : style
      }
      className={
        (autoResize ? 'resize-none overflow-hidden ' : 'resize-y ') +
        'min-w-0 flex-1 border-0 bg-transparent py-1 font-mono text-[14px] leading-[1.55] tracking-[-0.005em] text-ink outline-none placeholder:italic placeholder:text-ink-3/55 ' +
        className
      }
    />
  );
}
